import React, { useState } from 'react';
import { Document, PDFViewer, Font } from '@react-pdf/renderer';
import fontRegular from 'src/assets/fonts/Poppins-Regular.ttf';
import fontMedium from 'src/assets/fonts/Poppins-Medium.ttf';
import fontBold from 'src/assets/fonts/Poppins-Bold.ttf';
import _ from 'lodash';
import { FooterStartComponent, HtmlWrap, IncidentPage, RmpPage, ScenarioPage, SnapChartImageV2 } from '../comps';
import { auditEvent } from 'src/api/other';
import { useScenarioPdfQuery } from 'src/graphql/_generated';
import { IS_DEBUG } from '../conf';
import { plotLineGradient } from 'src/components/charts/util';
import { ColorSet } from 'src/redux/reducers/theme';
import { ActionsConf } from '../types';
import { useOrganisationBranding } from 'src/hooks/useOrganisationBranding';

Font.registerHyphenationCallback((word) => [word]);

Font.register({
  family: 'Poppins',

  fonts: [
    {
      src: fontRegular,
      fontWeight: 400,
    },
    {
      src: fontMedium,
      fontWeight: 500,
    },
    {
      src: fontBold,
      fontWeight: 600,
    },
  ],
});

interface PdfScenarioProps {
  assessmentId: string;
  scenarioId: string;
  isLogarithmic: boolean;
  onClose: () => void;
  includesRMP: boolean;
  includesIncidents: boolean;
  colors: ColorSet;
  includesActions: ActionsConf;
}

const PdfScenario: React.FC<PdfScenarioProps> = ({
  assessmentId,
  scenarioId,
  isLogarithmic,
  onClose,
  includesRMP,
  includesIncidents,
  colors,
  includesActions,
}) => {
  const { logoUrl: brandingLogo, secondaryColor: brandingSecondaryColor } = useOrganisationBranding();
  const [chart, setChart] = useState({ chart: '', hasChartsResults: false });

  const { data } = useScenarioPdfQuery({
    variables: {
      id: scenarioId,
    },
    onCompleted: (data) => {
      if (!data.scenario) {
        onClose();
      }
    },
    onError: onClose,
  });

  // FILE DOWNLOAD
  const print = (blob: Blob | undefined) => {
    if (blob) {
      let elm = document.createElement('a');
      elm.href = URL.createObjectURL(blob);
      elm.setAttribute('download', `${data?.scenario?.name || 'Scenario'}.pdf`);
      elm.click();
      elm.remove();

      auditEvent({
        action: 'ExportPdf',
        resource: 'scenario',
        scenario_id: scenarioId,
      });

      onClose();
    }
  };

  if (!data?.scenario) return null;

  const scenario = data.scenario;
  const scenarioCharts = [
    {
      data: scenario.baseRisk?.chart || [],
      upper: scenario.baseRisk?.upper || 0,
      lower: scenario.baseRisk?.lower || 0,
      color: colors.prussianBlue,
      fillColor: 'rgba(2, 3, 51, 0.07)',
      name: 'chart',
    },
    {
      data: scenario.liveRisk?.chart || [],
      upper: scenario.liveRisk?.upper || 0,
      lower: scenario.liveRisk?.lower || 0,
      name: 'live chart',
      color: plotLineGradient,
      fillColor: 'rgba(230, 46, 97, 0.07)',
    },
    {
      data: scenario.projectedRisk?.chart || [],
      upper: scenario.projectedRisk?.upper || 0,
      lower: scenario.projectedRisk?.lower || 0,
      name: 'complete chart',
      color: colors.brightBlue,
      fillColor: 'rgba(100, 149, 237, 0.07)',
    },
  ];

  return (
    <HtmlWrap $isTest={IS_DEBUG}>
      <SnapChartImageV2
        isLogarithmic={isLogarithmic}
        onSnap={(img) => setChart({ chart: img[0], hasChartsResults: true })}
        chartConf={[
          {
            charts: scenarioCharts,
          },
        ]}
        disabled={false}
        colors={colors}
      />

      {chart.hasChartsResults && (
        <PDFViewer>
          <Document
            onRender={(el) => {
              if (!IS_DEBUG) {
                print(el.blob);
              }
            }}
          >
            <ScenarioPage
              scenario={{ ...scenario, chartImage: chart.chart }}
              assessmentData={{ reviewDate: scenario.assessment?.reviewDate || undefined, id: assessmentId }}
              onRender={() => {}}
              colors={colors}
              includesActions={includesActions}
              logoSrc={brandingLogo}
              secondaryColor={brandingSecondaryColor}
            />
            {includesRMP &&
              scenario.riskManagementPlans?.map((rmp) => (
                <RmpPage
                  key={rmp.control?.id}
                  control={{
                    ...rmp.control!,
                    riskManagementPlans: [rmp], // Add the risk management plan back for consistency
                  }}
                  scenarios={[scenario]}
                  onRender={() => {}}
                  footerComponent={
                    <FooterStartComponent title={`${scenario.name} / Risk Management Plan / ${rmp.control?.name}`} />
                  }
                  colors={colors}
                  includesActions={includesActions}
                  logoSrc={brandingLogo}
                  secondaryColor={brandingSecondaryColor}
                />
              ))}
            {includesIncidents &&
              scenario.incidents?.map((inc) => (
                <IncidentPage
                  key={inc?.id}
                  incident={inc}
                  onRender={() => {}}
                  footerComponent={<FooterStartComponent title={`${scenario.name} / Incidents / ${inc?.name}`} />}
                  colors={colors}
                  includesActions={includesActions}
                  logoSrc={brandingLogo}
                  secondaryColor={brandingSecondaryColor}
                />
              ))}
          </Document>
        </PDFViewer>
      )}
    </HtmlWrap>
  );
};

export default PdfScenario;
